import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * Update-available banner — with `registerType: "prompt"` (see vite.config.ts)
 * a new service worker sits "waiting" instead of activating itself, so a
 * stale tab never silently mutates under the user. `needRefresh` flips true
 * once Workbox finds that waiting worker; tapping Update posts SKIP_WAITING
 * and reloads once the new SW takes control.
 *
 * Re-checks every 30 min while the tab is visible — PWAs are often left
 * open for hours, well past the load-time check vite-plugin-pwa does.
 */

const CHECK_INTERVAL_MS = 30 * 60 * 1000;

export function UpdatePrompt() {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, reg) {
      setRegistration(reg);
    },
    onRegisterError(error) {
      console.error("SW registration failed", error);
    },
  });

  useEffect(() => {
    if (!registration) return;
    const check = () => {
      if (document.visibilityState === "visible") void registration.update();
    };
    const id = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", check);
    };
  }, [registration]);

  if (!needRefresh) return null;

  const dismiss = () => setNeedRefresh(false);

  return (
    <div
      role="dialog"
      aria-label="Update available"
      data-ad-avoid=""
      className={cn(
        "fixed inset-x-3 bottom-3 z-50 mx-auto max-w-sm rounded-xl",
        "bg-bg-elevated border border-line shadow-warm-3 p-4 pr-3",
        "animate-in slide-in-from-bottom-4 fade-in duration-300",
      )}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-ink-subtle hover:bg-bg-sunken hover:text-ink"
      >
        <X className="h-4 w-4" />
      </button>
      <p
        className="font-display text-[18px] leading-tight tracking-tight text-ink m-0 pr-6"
        style={{ fontVariationSettings: `"opsz" 36, "wght" 500` }}
      >
        A new version of Travelix is ready
      </p>
      <p className="mt-1.5 text-[13px] leading-snug text-ink-muted m-0">
        Update now to get the latest fixes and features.
      </p>
      <Button
        size="sm"
        className="mt-3 gap-1.5"
        onClick={() => void updateServiceWorker(true)}
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Update
      </Button>
    </div>
  );
}
